import * as React from "react";
import PropTypes from "prop-types";
import { Button, Field } from "@lululemon/ui-library";
import classNames from "classnames";
import {
  trackEvent,
  EVENT_CATEGORY,
  EVENT_TYPE,
} from "@lululemon/mwa-analytics";

import Cookies from "js-cookie";
import styles from "./email-banner-form.module.scss";
import isEmail from "../../../util/is-email";
import WithUrqlClient from "../../../lib/with-urql-client";
import subscribeGuestEmail from "../../../graphql/subscribeGuestEmail";
import checkSubscriberExists from "../../../graphql/checkSubscriberExists";
import Disclaimer from "./disclaimer";

const EmailBannerForm = ({
  bannerProps,
  onSubscriptionSuccess,
  onSubscriptionFailure,
  graphqlClient,
  onSubmitStart,
  classes,
}) => {
  const [email, setEmail] = React.useState("");
  const [fieldHint, setFieldHint] = React.useState("");
  const [isHintVisible, setIsHintVisible] = React.useState(false);

  const {
    trackingLabel = "email-offer",
    eventTrackingId,
    modalSubmitButtonLabel,
    modalDisclaimer,
    errors: errorMessages,
    checkForExistingSubscriber = false,
    // TODO: refactor this, since "fieldProps" don't typically exist here
    fieldProps,
  } = bannerProps || {};

  const trackErrorEvent = (errorMessage, isGuestFacing = true) => {
    trackEvent(EVENT_CATEGORY.ERROR, {
      error: {
        errorType: `${trackingLabel}:submission-error`,
        errorDetails: errorMessage,
        errorGuestFacing: isGuestFacing,
        errorMessage: errorMessage,
      },
    });
  };

  React.useEffect(() => {
    trackEvent(EVENT_CATEGORY.COMPONENT_EVENT, {
      eventSubType: EVENT_TYPE.IMPRESSION,
      component: {
        id: `${trackingLabel}-view`,
        type: "modal",
        text: modalSubmitButtonLabel,
        position: -1,
        totalCount: -1,
      },
    });
    // Disable deps b/c we only want this event to run once, on component mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isEmailValid = (value, shouldTrackError) => {
    if (!value) {
      setFieldHint(errorMessages?.["empty-email"]);

      if (shouldTrackError) {
        trackErrorEvent(errorMessages?.["empty-email"]);
      }

      return false;
    }

    if (!isEmail(value)) {
      setFieldHint(errorMessages?.["invalid-email"]);

      if (shouldTrackError) {
        trackErrorEvent(errorMessages?.["invalid-email"]);
      }

      return false;
    }

    setFieldHint("");
    return true;
  };

  const isExistingSubscriber = async (email, shouldTrackError) => {
    if (!checkForExistingSubscriber) {
      return false;
    }
    try {
      const value = await checkSubscriberExists(
        { guestEmail: email },
        graphqlClient
      );

      if (value) {
        setFieldHint(errorMessages?.["already-subscribed"]);
        if (shouldTrackError) {
          trackErrorEvent(errorMessages?.["already-subscribed"]);
        }
        return true;
      }
      return false;
    } catch (error) {
      trackErrorEvent(error.message, false);
      return false;
    }
  };

  const handleOnChange = (event) => {
    const fieldValue = event.target.value;
    setEmail(fieldValue);
    setIsHintVisible(!isEmailValid(fieldValue, false));
  };

  const handleOnSubmit = async (event) => {
    event.preventDefault();

    await trackEvent(EVENT_CATEGORY.COMPONENT_EVENT, {
      component: {
        id: `${trackingLabel}-cta-click`,
        type: "button",
        text: modalSubmitButtonLabel,
        position: -1,
        totalCount: -1,
      },
    });

    // Prevent bad email from being submitted
    const isInvalidEmail = !isEmailValid(email, true);
    setIsHintVisible(isInvalidEmail);
    if (isInvalidEmail) return;

    // Check if email is already subscribed
    const isAlreadySubscribed = await isExistingSubscriber(email, true);
    setIsHintVisible(isAlreadySubscribed);
    if (isAlreadySubscribed) return;

    onSubmitStart();

    const locale = Cookies?.get("UsrLocale")?.replace("_", "-") || "en-US";
    const [language, countryCode] = locale?.split("-");
    function getAquisitionSource() {
      if (bannerProps.aquisitionSource) {
        return bannerProps.aquisitionSource;
      }
      return countryCode.toLowerCase() === "us"
        ? "web_us_banner"
        : "web_ca_banner";
    }
    const aquisitionSource = getAquisitionSource();

    try {
      await subscribeGuestEmail(
        {
          guestEmail: email,
          locale,
          language,
          countryCode,
          eventTrackingId,
          aquisitionSource,
        },
        graphqlClient
      );

      trackEvent(EVENT_CATEGORY.APP_RESPONSE, {
        id: "email-signup",
        event: {
          attributes: {
            details: trackingLabel,
          },
        },
      });

      onSubscriptionSuccess?.();
    } catch (error) {
      trackErrorEvent(error.message, false);
      onSubscriptionFailure?.();
    }
  };

  return (
    <form
      onSubmit={handleOnSubmit}
      className={classNames(styles.modalBannerForm, classes?.modalBannerForm)}
    >
      <div
        className={classNames(
          styles.emailFieldWrapper,
          classes?.emailFieldWrapper
        )}
      >
        <Field
          required={false}
          id="email-address"
          name="email-address"
          placeholder={fieldProps?.placeholder ?? "Email address"}
          value={email}
          onChange={handleOnChange}
          status={isHintVisible ? "error" : null}
          hint={isHintVisible ? fieldHint : null}
          hideLabel={fieldProps?.hideLabel ?? true}
        >
          {fieldProps?.label ?? "Email Address"}
        </Field>
      </div>
      {modalDisclaimer && <Disclaimer modalDisclaimer={modalDisclaimer} />}
      <Button
        data-testid="subscribeCTA"
        type="submit"
        variant="filledImpact"
        block
      >
        {modalSubmitButtonLabel}
      </Button>
    </form>
  );
};

EmailBannerForm.defaultProps = {
  onSubscriptionSuccess: () => {},
  onSubscriptionFailure: () => {},
  onSubmitStart: () => {},
};

EmailBannerForm.propTypes = {
  bannerProps: PropTypes.shape({
    modalSubmitButtonLabel: PropTypes.string.isRequired,
    modalDisclaimer: PropTypes.any,
    eventTrackingId: PropTypes.string,
    aquisitionSource: PropTypes.string,
    trackingLabel: PropTypes.string,
    checkForExistingSubscriber: PropTypes.bool,
    errors: PropTypes.shape({
      "empty-email": PropTypes.string.isRequired,
      "invalid-email": PropTypes.string.isRequired,
      "already-subscribed": PropTypes.string,
    }),
  }).isRequired,

  graphqlClient: PropTypes.shape({
    query: PropTypes.func.isRequired,
    mutation: PropTypes.func.isRequired,
    url: PropTypes.string.isRequired,
  }).isRequired,
  onSubscriptionSuccess: PropTypes.func,
  onSubscriptionFailure: PropTypes.func,
  onSubmitStart: PropTypes.func,
};

export { EmailBannerForm };
export default WithUrqlClient(EmailBannerForm);
